import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { defer, Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { TokenExpirationMonitorService } from '../services/auth/token-expiration-monitor.service';

interface RefreshAccessTokenResponse {
  access_token: string;
}

const refreshesByAuthService = new WeakMap<
  AuthService,
  Map<number, Observable<RefreshAccessTokenResponse>>
>();

function addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
}

function isPublicAuthRequest(request: HttpRequest<any>): boolean {
  return request.url.includes('/auth/validate-recover-token') ||
    request.url.includes('/auth/generate-recover-token') ||
    request.url.includes('/auth/login');
}

function isRefreshRequest(request: HttpRequest<any>): boolean {
  const path = request.url.split(/[?#]/, 1)[0].replace(/\/+$/, '');
  return path.endsWith('/auth/refresh');
}

function refreshesFor(authService: AuthService): Map<number, Observable<RefreshAccessTokenResponse>> {
  let refreshes = refreshesByAuthService.get(authService);
  if (!refreshes) {
    refreshes = new Map<number, Observable<RefreshAccessTokenResponse>>();
    refreshesByAuthService.set(authService, refreshes);
  }
  return refreshes;
}

function refreshForSession(
  authService: AuthService,
  tokenMonitor: TokenExpirationMonitorService,
  sessionGeneration: number
): Observable<RefreshAccessTokenResponse> {
  const sessionRefreshes = refreshesFor(authService);
  const existingRefresh = sessionRefreshes.get(sessionGeneration);
  if (existingRefresh) {
    return existingRefresh;
  }

  let sharedRefresh!: Observable<RefreshAccessTokenResponse>;
  sharedRefresh = defer(() => authService.refreshToken()).pipe(
    tap(() => {
      if (authService.getSessionGeneration() !== sessionGeneration || authService.isLoggingOut()) {
        throw new Error('Session changed during token refresh');
      }

      tokenMonitor.startMonitoring();
      if (typeof BroadcastChannel !== 'undefined') {
        const broadcastChannel = new BroadcastChannel('precredit_session_channel');
        broadcastChannel.postMessage({ type: 'TOKEN_REFRESHED' });
        broadcastChannel.close();
      }
    }),
    catchError(error => {
      if (
        authService.getSessionGeneration() === sessionGeneration &&
        !authService.isLoggingOut()
      ) {
        const reason = error?.headers?.get?.('X-Session-Expired-Reason') ||
          error?.error?.expiredReason || '';
        authService.forceLogoutWithReason(reason);
      }
      return throwError(() => error);
    }),
    finalize(() => {
      if (sessionRefreshes.get(sessionGeneration) === sharedRefresh) {
        sessionRefreshes.delete(sessionGeneration);
      }
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  sessionRefreshes.set(sessionGeneration, sharedRefresh);
  return sharedRefresh;
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  tokenMonitor: TokenExpirationMonitorService,
  requestAccessToken: string,
  requestSessionGeneration: number,
  originalError: HttpErrorResponse
): Observable<HttpEvent<any>> {
  if (
    authService.getSessionGeneration() !== requestSessionGeneration ||
    authService.isLoggingOut()
  ) {
    return throwError(() => originalError);
  }

  const currentAccessToken = authService.getAccessToken();
  if (!currentAccessToken) {
    return throwError(() => originalError);
  }

  // A request sent with the previous token can fail after another request has
  // already refreshed the same session. Reuse that token rather than refreshing again.
  if (currentAccessToken !== requestAccessToken) {
    return next(addTokenToRequest(request, currentAccessToken));
  }

  const expirationTime = authService.getSessionExpirationTime();
  if (expirationTime && Date.now() >= expirationTime) {
    authService.logout();
    return throwError(() => new Error('Token expired'));
  }

  return refreshForSession(authService, tokenMonitor, requestSessionGeneration).pipe(
    switchMap((refreshResponse: RefreshAccessTokenResponse) => {
      if (
        authService.getSessionGeneration() !== requestSessionGeneration ||
        authService.isLoggingOut()
      ) {
        return throwError(() => new Error('Session changed during token refresh'));
      }

      return next(addTokenToRequest(request, refreshResponse.access_token));
    })
  );
}

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const tokenMonitor = inject(TokenExpirationMonitorService);
  const accessToken = authService.getAccessToken();
  const sessionGeneration = authService.getSessionGeneration();
  let authorizedRequest = request;

  const refreshHasExplicitAuthorization = isRefreshRequest(request) && request.headers.has('Authorization');
  if (accessToken && !refreshHasExplicitAuthorization) {
    authorizedRequest = addTokenToRequest(request, accessToken);
  }

  if (!isPublicAuthRequest(request) && !isRefreshRequest(request) && accessToken) {
    tokenMonitor.resetIdleTimer();
  }

  return next(authorizedRequest).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        if (isPublicAuthRequest(request) || isRefreshRequest(request)) {
          return throwError(() => error);
        }
        if (accessToken) {
          return handle401Error(
            request,
            next,
            authService,
            tokenMonitor,
            accessToken,
            sessionGeneration,
            error
          );
        }
      }
      return throwError(() => error);
    })
  );
};
